import React, { useEffect, useMemo, useRef } from 'react';
import CodeMirror from '@uiw/react-codemirror';
import { oneDark } from '@codemirror/theme-one-dark';
import { cssLanguage } from '@codemirror/lang-css';
import { htmlLanguage } from '@codemirror/lang-html';
import { javascriptLanguage, jsxLanguage, tsxLanguage, typescriptLanguage } from '@codemirror/lang-javascript';
import { jsonLanguage } from '@codemirror/lang-json';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView, drawSelection, keymap, lineNumbers } from '@codemirror/view';
import { indentWithTab } from '@codemirror/commands';
import { useAppSelector } from '../../hooks/useAppRedux';
import { selectTheme } from '../../redux/slices/appSlice';

const languageCompartment = new Compartment();

const getLanguageExtension = (language) => {
  if (language === 'tsx') {
    return tsxLanguage;
  }

  if (language === 'ts') {
    return typescriptLanguage;
  }

  if (language === 'jsx') {
    return jsxLanguage;
  }

  if (language === 'js') {
    return javascriptLanguage;
  }

  if (language === 'json') {
    return jsonLanguage;
  }

  if (language === 'css') {
    return cssLanguage;
  }

  if (language === 'html') {
    return htmlLanguage;
  }

  return [];
};

const sharedEditorTheme = {
  '&': {
    height: '100%',
    fontSize: '13px',
    direction: 'ltr',
    textAlign: 'left'
  },
  '.cm-editor': {
    height: '100%'
  },
  '.cm-scroller': {
    height: '100%',
    overflow: 'auto',
    fontFamily: '"Cascadia Code", "Fira Code", "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, Liberation Mono, monospace',
    lineHeight: '1.7',
    direction: 'ltr'
  },
  '.cm-content': {
    minHeight: '100%',
    direction: 'ltr',
    textAlign: 'left'
  },
  '.cm-line': {
    paddingLeft: '8px'
  },
  '.cm-focused': {
    outline: 'none'
  }
};

const darkEditorTheme = EditorView.theme({
  ...sharedEditorTheme,
  '&': {
    ...sharedEditorTheme['&'],
    backgroundColor: '#1e1e1e',
    color: '#d4d4d4'
  },
  '.cm-gutters': {
    backgroundColor: '#1e1e1e',
    color: '#858585',
    border: 'none',
    minWidth: '44px'
  },
  '.cm-activeLine': {
    backgroundColor: '#2a2d2e'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#2a2d2e',
    color: '#c6c6c6'
  },
  '.cm-cursor': {
    borderLeftColor: '#aeafad'
  },
  '.cm-selectionBackground': {
    backgroundColor: 'rgba(96, 165, 250, 0.38) !important'
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: 'rgba(96, 165, 250, 0.48) !important'
  },
  '.cm-selectionLayer .cm-selectionBackground': {
    backgroundColor: 'rgba(96, 165, 250, 0.38) !important'
  },
  '&.cm-focused .cm-selectionLayer .cm-selectionBackground': {
    backgroundColor: 'rgba(96, 165, 250, 0.48) !important'
  },
  '.cm-content ::selection': {
    backgroundColor: 'rgba(96, 165, 250, 0.48) !important'
  },
  '.cm-line ::selection': {
    backgroundColor: 'rgba(96, 165, 250, 0.48) !important'
  },
  '.cm-content': {
    ...sharedEditorTheme['.cm-content'],
    caretColor: '#aeafad'
  },
  '.cm-panels': {
    backgroundColor: '#252526',
    color: '#d4d4d4'
  },
  '.cm-foldPlaceholder': {
    backgroundColor: '#2d2d30',
    border: '1px solid #454545',
    color: '#cccccc'
  }
}, { dark: true });

const lightEditorTheme = EditorView.theme({
  ...sharedEditorTheme,
  '&': {
    ...sharedEditorTheme['&'],
    backgroundColor: '#ffffff',
    color: '#1f2937'
  },
  '.cm-gutters': {
    backgroundColor: '#f7f7f9',
    color: '#94a3b8',
    borderRight: '1px solid #e5e7eb',
    minWidth: '44px'
  },
  '.cm-activeLine': {
    backgroundColor: '#f3f4f6'
  },
  '.cm-activeLineGutter': {
    backgroundColor: '#f3f4f6',
    color: '#334155'
  },
  '.cm-cursor': {
    borderLeftColor: '#0f172a'
  },
  '.cm-selectionBackground': {
    backgroundColor: 'rgba(37, 99, 235, 0.28) !important'
  },
  '&.cm-focused .cm-selectionBackground': {
    backgroundColor: 'rgba(37, 99, 235, 0.38) !important'
  },
  '.cm-selectionLayer .cm-selectionBackground': {
    backgroundColor: 'rgba(37, 99, 235, 0.28) !important'
  },
  '&.cm-focused .cm-selectionLayer .cm-selectionBackground': {
    backgroundColor: 'rgba(37, 99, 235, 0.38) !important'
  },
  '.cm-content ::selection': {
    backgroundColor: 'rgba(37, 99, 235, 0.38) !important'
  },
  '.cm-line ::selection': {
    backgroundColor: 'rgba(37, 99, 235, 0.38) !important'
  },
  '.cm-content': {
    ...sharedEditorTheme['.cm-content'],
    caretColor: '#0f172a'
  },
  '.cm-panels': {
    backgroundColor: '#f8fafc',
    color: '#1f2937'
  },
  '.cm-foldPlaceholder': {
    backgroundColor: '#f3f4f6',
    border: '1px solid #cbd5e1',
    color: '#475569'
  }
});

export const CodeEditor = ({ value, onChange, language = 'jsx', onSave, ariaLabel, filePath }) => {
  const theme = useAppSelector(selectTheme);
  const viewRef = useRef(null);
  const activeValueRef = useRef(value || '');
  const languageExtension = useMemo(() => getLanguageExtension(language), [language]);
  const editorTheme = useMemo(() => (theme === 'dark' ? darkEditorTheme : lightEditorTheme), [theme]);

  useEffect(() => {
    activeValueRef.current = value || '';

    const view = viewRef.current;
    if (!view) {
      return;
    }

    const currentValue = view.state.doc.toString();
    if (currentValue !== activeValueRef.current) {
      view.dispatch({
        changes: {
          from: 0,
          to: currentValue.length,
          insert: activeValueRef.current
        }
      });
    }
  }, [filePath, value]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) {
      return;
    }

    view.dispatch({
      effects: languageCompartment.reconfigure(languageExtension)
    });
  }, [languageExtension]);

  const extensions = useMemo(
    () => [
      lineNumbers(),
      drawSelection(),
      keymap.of([
        indentWithTab,
        {
          key: 'Mod-s',
          run: () => {
            onSave?.();
            return true;
          }
        }
      ]),
      languageCompartment.of(languageExtension),
      EditorState.tabSize.of(2),
      EditorView.lineWrapping,
      editorTheme
    ],
    [editorTheme, languageExtension, onSave]
  );

  return (
    <div
      className={`relative flex h-full min-h-[420px] flex-1 overflow-hidden ${
        theme === 'dark' ? 'bg-[#1e1e1e]' : 'bg-white'
      }`}
      dir="ltr"
    >
      <div className="h-full min-h-[420px] flex-1 overflow-hidden [direction:ltr]">
        <CodeMirror
          className="h-full"
          key={filePath || language}
          value={value || ''}
          height="100%"
          theme={theme === 'dark' ? oneDark : 'light'}
          basicSetup={{
            foldGutter: true,
            highlightActiveLine: true,
            highlightActiveLineGutter: true,
            syntaxHighlighting: true
          }}
          extensions={extensions}
          aria-label={ariaLabel}
          style={{ direction: 'ltr', textAlign: 'left' }}
          onCreateEditor={(view) => {
            viewRef.current = view;
          }}
          onChange={(nextValue) => {
            activeValueRef.current = nextValue;
            onChange(nextValue);
          }}
        />
      </div>
    </div>
  );
};
